import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Calculator, ArrowLeft, Smartphone, Battery, Camera, Droplets, CircuitBoard, Plug, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { phoneRepairData } from '../mock';
import HeaderSocials from '../components/HeaderSocials';

const PhonesPage = () => {
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedRepair, setSelectedRepair] = useState('');
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [repairData, setRepairData] = useState(phoneRepairData);
  const [seo, setSeo] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/calculator-data/phone`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && Object.keys(d).length) setRepairData(d); })
      .catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/page-content/phones`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && d.content && Object.keys(d.content).length) setSeo(d.content); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const description = (seo && seo.metaDescription) || 'Професійний ремонт телефонів iPhone, Samsung, Xiaomi, Huawei в Одесі. Заміна екрану, батареї, камери. Безкоштовна діагностика. Гарантія.';
    let descriptionTag = document.head.querySelector('meta[name="description"]');
    let canonicalTag = document.head.querySelector('link[rel="canonical"]');

    if (!descriptionTag) {
      descriptionTag = document.createElement('meta');
      descriptionTag.setAttribute('name', 'description');
      document.head.appendChild(descriptionTag);
    }

    if (!canonicalTag) {
      canonicalTag = document.createElement('link');
      canonicalTag.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalTag);
    }

    descriptionTag.setAttribute('content', description);
    canonicalTag.setAttribute('href', 'https://cimm.od.ua/phones');
  }, [seo]);

  const brands = Object.keys(repairData);
  const models = selectedBrand ? repairData[selectedBrand].models : [];
  const repairs = selectedBrand ? Object.keys(repairData[selectedBrand].repairs) : [];

  const handleCalculate = () => {
    if (selectedBrand && selectedModel && selectedRepair) {
      const price = repairData[selectedBrand].repairs[selectedRepair][selectedModel];
      setCalculatedPrice(price);
    }
  };

  const handleReset = () => {
    setSelectedBrand('');
    setSelectedModel('');
    setSelectedRepair('');
    setCalculatedPrice(null);
  };

  const repairServices = [
    {
      icon: Smartphone,
      title: 'Заміна екрану',
      description: 'Професійна заміна розбитого або пошкодженого дисплея',
      details: [
        'Використовуємо оригінальні дисплеї та якісні копії',
        'Ремонт від 30 хвилин до 1 години',
        'Гарантія на встановлений екран',
        'Безкоштовна діагностика сенсора'
      ],
      price: 'від 800 грн'
    },
    {
      icon: Battery,
      title: 'Заміна батареї',
      description: 'Заміна акумулятора на оригінальну або якісну копію',
      details: [
        'Оригінальні акумулятори з гарантією',
        'Швидка заміна - 20-30 хвилин',
        'Перевірка ємності після заміни',
        'Рекомендації по експлуатації'
      ],
      price: 'від 400 грн'
    },
    {
      icon: Camera,
      title: 'Заміна камери',
      description: 'Ремонт або заміна основної та фронтальної камери',
      details: [
        'Заміна основної та фронтальної камер',
        'Ремонт модуля камери',
        'Перевірка якості знімків',
        'Калібрування фокусування'
      ],
      price: 'від 700 грн'
    },
    {
      icon: Droplets,
      title: 'Ремонт після води',
      description: 'Відновлення телефона після потрапляння води або рідини',
      details: [
        'Професійна чистка від окислення',
        'Ультразвукове очищення плати',
        'Заміна пошкоджених компонентів',
        'Повна діагностика всіх функцій'
      ],
      price: 'від 600 грн'
    },
    {
      icon: CircuitBoard,
      title: 'Ремонт плати',
      description: 'Ремонт материнської плати телефону',
      details: [
        'Мікросхемний ремонт плати',
        'Заміна пошкоджених мікросхем',
        'Відновлення доріжок та контактів',
        'Діагностика на професійному обладнанні'
      ],
      price: 'від 800 грн'
    },
    {
      icon: Plug,
      title: "Заміна роз'єму зарядки",
      description: 'Ремонт або заміна роз\'єму зарядки',
      details: [
        "Заміна роз'єму Lightning, USB-C, Micro-USB",
        'Чистка роз\'єму від бруду',
        'Ремонт контактів',
        'Перевірка швидкості зарядки'
      ],
      price: 'від 400 грн'
    }
  ];

  return (
    <div className="homepage">
      <Helmet>
        <title>{(seo && seo.metaTitle) || 'Ремонт телефонів в Одесі - CIMM | Apple, Samsung, Xiaomi, Huawei'}</title>
        <meta name="keywords" content={(seo && seo.metaKeywords) || "ремонт телефонів Одеса, ремонт iPhone Одеса, ремонт Samsung Одеса, заміна екрану телефону, заміна батареї телефону"} />
      </Helmet>

      {/* Header */}
      <header className="cyber-header fixed top-0 left-0 right-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="text-2xl font-bold neon-text">CIMM</div>
            <div className="text-sm text-gray-400 hidden md:block">Комп'ютери та Ремонт</div>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-gray-300 hover:text-orange-500 transition-all duration-300">Головна</Link>
            <Link to="/phones" className="text-orange-500">Телефони</Link>
            <Link to="/laptops" className="text-gray-300 hover:text-orange-500 transition-all duration-300">Ноутбуки</Link>
            <Link to="/laptop-cleaning" className="text-gray-300 hover:text-orange-500 transition-all duration-300">Чистка</Link>
            <Link to="/contacts" className="text-gray-300 hover:text-orange-500 transition-all duration-300">Контакти</Link>
          </nav>
          <div className="flex items-center gap-2">
            <HeaderSocials />
            <a href="tel:+380974178788" className="neon-text font-semibold hover:drop-shadow-[0_0_15px_rgba(255,140,0,0.8)] transition-all duration-300" data-testid="header-phone-link">
              +38 097 417 8788
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <section className="pt-32 pb-20 px-4 dark-section min-h-screen">
        <div className="container mx-auto max-w-6xl relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-orange-500 mb-8 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            Повернутися на головну
          </Link>

          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Ремонт <span className="neon-text">телефонів</span> в Одесі
            </h1>
            <p className="text-xl text-gray-400">
              Професійний ремонт Apple, Samsung, Xiaomi, Huawei
            </p>
            <Link
              to="/remont-iphone"
              className="mt-5 inline-flex text-orange-400 underline underline-offset-4 hover:text-orange-300"
              data-testid="iphone-repair-internal-link"
            >
              Ремонт iPhone в Одесі: акумулятор, дисплей і роз'єм
            </Link>
          </div>

          {/* Calculator Section - Moved to top */}
          <div className="mb-16" id="calculator">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              <span className="neon-text">Калькулятор</span> вартості ремонту
            </h2>
            
            <div className="glass-card p-8">
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Виробник</label>
                  <Select value={selectedBrand} onValueChange={(value) => {
                    setSelectedBrand(value);
                    setSelectedModel('');
                    setSelectedRepair('');
                    setCalculatedPrice(null);
                  }}>
                    <SelectTrigger className="calc-select bg-white/5 border-orange-500/30">
                      <SelectValue placeholder="Оберіть виробника" />
                    </SelectTrigger>
                    <SelectContent className="calc-dropdown">
                      {brands.map((brand) => (
                        <SelectItem key={brand} value={brand} className="calc-dropdown-item">
                          {brand}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Модель</label>
                  <Select 
                    value={selectedModel} 
                    onValueChange={(value) => {
                      setSelectedModel(value);
                      setCalculatedPrice(null);
                    }}
                    disabled={!selectedBrand}
                  >
                    <SelectTrigger className="calc-select bg-white/5 border-orange-500/30 disabled:opacity-50">
                      <SelectValue placeholder="Оберіть модель" />
                    </SelectTrigger>
                    <SelectContent className="calc-dropdown max-h-[300px]">
                      {models.map((model) => (
                        <SelectItem key={model} value={model} className="calc-dropdown-item">
                          {model}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Тип поломки</label>
                  <Select 
                    value={selectedRepair} 
                    onValueChange={(value) => {
                      setSelectedRepair(value);
                      setCalculatedPrice(null);
                    }}
                    disabled={!selectedBrand}
                  >
                    <SelectTrigger className="calc-select bg-white/5 border-orange-500/30 disabled:opacity-50">
                      <SelectValue placeholder="Оберіть тип поломки" />
                    </SelectTrigger>
                    <SelectContent className="calc-dropdown">
                      {repairs.map((repair) => (
                        <SelectItem key={repair} value={repair} className="calc-dropdown-item">
                          {repair}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  onClick={handleCalculate}
                  disabled={!selectedBrand || !selectedModel || !selectedRepair}
                  className="flex-1 futuristic-btn bg-orange-500 hover:bg-orange-600 text-white border-0 disabled:opacity-50"
                  size="lg"
                >
                  <Calculator className="w-5 h-5 mr-2" />
                  Розрахувати вартість
                </Button>
                <Button
                  onClick={handleReset}
                  variant="outline"
                  className="neon-border border-orange-500 text-orange-500 hover:bg-orange-500/10 bg-transparent"
                  size="lg"
                >
                  Скинути
                </Button>
              </div>

              {calculatedPrice && (
                <div className="mt-8 p-6 bg-gray-900/50 rounded-xl border border-orange-500/30 animate-fade-in-up">
                  {typeof calculatedPrice === 'object' && calculatedPrice.original !== undefined ? (
                    // Display two prices (original and copy)
                    <div className="space-y-4">
                      <p className="text-gray-300 text-lg text-center mb-4">Орієнтовна вартість ремонту:</p>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="p-4 bg-white/5 rounded-lg border border-orange-500/30">
                          <p className="text-gray-400 text-sm mb-2 text-center">Оригінальна деталь</p>
                          <p className="text-4xl font-bold neon-text text-center">{calculatedPrice.original} грн</p>
                        </div>
                        <div className="p-4 bg-white/5 rounded-lg border border-orange-500/30">
                          <p className="text-gray-400 text-sm mb-2 text-center">Копія (аналог)</p>
                          <p className="text-4xl font-bold text-green-400 text-center">{calculatedPrice.copy} грн</p>
                        </div>
                      </div>
                      <p className="text-gray-400 text-sm mt-4 text-center">
                        * Точна вартість визначається після безкоштовної діагностики
                      </p>
                    </div>
                  ) : (
                    // Display single price
                    <div className="text-center">
                      <p className="text-gray-300 text-lg mb-2">Орієнтовна вартість ремонту:</p>
                      <p className="text-5xl font-bold neon-text">{calculatedPrice} грн</p>
                      <p className="text-gray-400 text-sm mt-4">
                        * Точна вартість визначається після безкоштовної діагностики
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-6 p-4 bg-gray-900/30 rounded-lg border border-orange-500/20">
                <p className="text-gray-400 text-sm text-center">
                  <span className="text-orange-500 font-semibold">Увага:</span> Вказані ціни є орієнтовними.
                  Остаточна вартість ремонту визначається майстром після діагностики пристрою.
                </p>
              </div>
            </div>
          </div>

          {/* Repair Services Info - Moved after calculator */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Наші <span className="neon-text">послуги</span></h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {repairServices.map((service, index) => {
                const IconComponent = service.icon;
                // Определяем ссылку для каждой услуги
                const getServiceLink = (index) => {
                  if (index === 0) return '/phones/screen-repair'; // Заміна екрану
                  if (index === 1) return '/phones/battery-repair'; // Заміна батареї
                  return '#calculator'; // Остальные ведут к калькулятору
                };
                
                const isClickable = true; // Все карточки кликабельны
                
                const CardContent = () => (
                  <>
                    <div className="w-14 h-14 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-xl flex items-center justify-center mb-4">
                      <IconComponent className="w-7 h-7 text-orange-500 icon-glow" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{service.title}</h3>
                    <p className="text-gray-400 mb-4">{service.description}</p>
                    <ul className="space-y-2 mb-4">
                      {service.details.map((detail, idx) => (
                        <li key={idx} className="text-sm text-gray-300 flex items-start gap-2">
                          <span className="text-orange-500 mt-1">•</span>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="text-orange-500 font-bold">{service.price}</div>
                    {(
                      <div className="mt-4 flex items-center gap-2 text-orange-500 opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="text-sm font-semibold">Детальніше</span>
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    )}
                  </>
                );
                
                return (
                  <Link 
                    key={index}
                    to={getServiceLink(index)}
                    className="glass-card p-6 hover:scale-105 transition-transform duration-300 block group cursor-pointer"
                  >
                    <CardContent />
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PhonesPage;